import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import DataTable from '../../../components/DataTable/DataTable';
import { ItemRevisionProps } from '../../../types/IdProps';
import Title from '../../../components/Headers/Title';
import Form from '../../../components/Fields/Form';
import { FormConfig } from '../../../components/Fields/Types';

import DataService from "../../../services/data.service";
import { ApplicationFunctions, useAuth } from '../../../contexts/authContext';
import useValidation from '../../../components/Hooks/ValidationHook/useValidation';
import Unauthorized from '../../Authentication/Unauthorized';
import SelectListField from '../../../components/Fields/SelectListField';
import SwitchField from '../../../components/Fields/SwitchField';
import ButtonBar from '../../../components/Fields/ButtonBar';
import Authorize from '../../../components/AuthComponent/Authorize';
import Button from '../../../components/Buttons/Button';
import LicenseQueryModel from '../../../models/LicenseQueryModel';
import useSelectListOptions from '../../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import { BusinessPartnerTypeEnum } from '../../../enums/BusinessPartnerTypeEnum';
import { useNotifications } from '../../../contexts/notificationContext';
import ItemRevisionModel from '../../../models/ItemRevisionModel';
import { IColInfo } from '../../../components/DataTable/Interfaces';

interface ILicenseFilter {
    customerId?: number
    countryId?: number
    categoryId?: number
    brandId?: number
    activeOnly: boolean
    validOnly: boolean
}

const ItemRevision_tab_Licenses: React.FC<ItemRevisionProps> = ({ itemId, itemRevisionId, itemTypeId }) => {
    const [formData, setFormData] = useState<ILicenseFilter>({ activeOnly: true, validOnly: true })
    const [tableData, setTableData] = useState<LicenseQueryModel[]>();
    const customers = useSelectListOptions('api/v1/selectlist/BusinessPartners?businessPartnerTypeId=' + BusinessPartnerTypeEnum.Customer, '-- Select customer');
    const countries = useSelectListOptions('api/v1/selectlist/Countries', '-- Select country');
    const categories = useSelectListOptions('api/v1/selectlist/ProductCategoryNameTrees?activeOnly=' + formData.activeOnly + '&itemId=' + itemId);
    const brands = useSelectListOptions('api/v1/selectlist/BrandNameTrees?activeOnly=' + formData.activeOnly + '&itemId=' + itemId);
    const { handleException } = useNotifications();

    useEffect(() => {
        if (itemRevisionId) {
            DataService.getById<ItemRevisionModel>('api/v1/itemrevisions/', itemRevisionId)
                .then(response => {
                    formData.brandId = response.brandId
                    formData.categoryId = response.productCategoryId
                    setFormData({ ...formData });
                    getTableData();
                })
                .catch(e => handleException(e));
        }
    }, [itemRevisionId, handleException]);

    const colInfos: IColInfo[] = [
        { key: true, dataProp: 'id', visible: false },
        { title: 'Name', dataProp: 'name', filterInfo: { filterType: 'Textbox' } },
        { title: 'Licensor', dataProp: 'licensor', filterInfo: { filterType: 'CheckboxList' } },
        { title: 'Licensee', dataProp: 'licensee', filterInfo: { filterType: 'CheckboxList' } },
        { title: 'Summary', dataProp: 'summary', filterInfo: { filterType: 'Textbox' } },
        { title: 'Regions', dataProp: 'regions', filterInfo: { filterType: 'Textbox' } },
        { title: 'Countries', dataProp: 'countries', width: 400, filterInfo: { filterType: 'Textbox' } },
        { title: 'Valid', dataProp: 'isValid', dataType: 'bool', width: 75, filterInfo: { filterType: 'CheckboxList' } },
        { title: 'Begin date', dataProp: 'beginDate', dataType: 'date' },
        { title: 'End date', dataProp: 'endDate', dataType: 'date' },
    ];

    function submit() {
        getTableData();
    }

    function getTableData() {
        DataService
            .post<LicenseQueryModel[]>(`api/v1/Licenses/Search/`, formData)
            .then(response => {
                console.log(response)
                setTableData(response);
            })
            .catch(e => { handleException(e); });
    }

    function onFieldChanged(name: string, value: string): void {
        formData[name] = value;
        setFormData(formData);
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'customerId', label: 'Customer', labelSize:3 },
            { name: 'countryId', label: 'Country', labelSize:3},
            { name: 'categoryId', label: 'Category' },
            { name: 'brandId', label: 'Brand' },
            { name: 'activeOnly', label: 'Active licenses only', labelSize: 8 },
            { name: 'validOnly', label: 'Valid licenses only', labelSize: 8 },
        ],
        handleSubmit: submit,
        handleFieldChanged: onFieldChanged,
        showErrors: 'always',
    };

    const { getLabelProps, getControlProps, getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData);

    const { isAuthorizedFor } = useAuth();
    if (!isAuthorizedFor(ApplicationFunctions.ViewLicense))
        return (<Unauthorized />);

    return (
        <div className='main-content'>
            <Form title='Licenses' footer={(<>
                <ButtonBar offset={2}>
                    <Button text='Search' {...getSubmitButtonProps()} />
                </ButtonBar>
            </>)}>
                <Container fluid>
                    <Row>
                        <Col md='4'>
                            <SelectListField {...getFieldProps('customerId')} options={customers} />
                            <SelectListField {...getFieldProps('countryId')} options={countries} />
                        </Col>
                        <Col md='6'>
                            <SelectListField {...getFieldProps('categoryId')} options={categories} />
                            <SelectListField {...getFieldProps('brandId')} options={brands} />
                        </Col>
                        <Col md='2'>
                            <SwitchField {...getFieldProps('activeOnly')} />
                            <SwitchField {...getFieldProps('validOnly')} />
                        </Col>
                    </Row>
                </Container>
            </Form>
            <Container fluid>
                <Row>
                    <Col>
                        <DataTable
                            name='dtItemRevisionLicenses'
                            colInfos={colInfos}
                            tableData={tableData}
                            navigateTitle='License'
                            navigateUrl='/plm/Licenses/Details/'
                        />
                    </Col>
                </Row>
            </Container>

        </div>
    );
}
export default ItemRevision_tab_Licenses;
